import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../services/api';
import api from '../services/api';
import ProductCard from '../components/ProductCard';
import GoogleReviews from '../components/GoogleReviews';
import VisualEffect from '../components/VisualEffect';
import '../styles/Home.css';

// Icones pour les catégories principales
const categoryIcons = {
  'femme': '👗',
  'homme': '👔',
  'enfant': '👶',
  'maison': '🏠',
  'electromenager': '🔌',
  'jeux-video': '🎮',
  'media': '📚',
  'informatique': '💻',
  'sport': '⚽',
  'brocante': '🏺',
  'voitures': '🚗'
};

// Composant compteur animé
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isVisible = useRef(false);

  // Observer la visibilité
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { isVisible.current = entry.isIntersecting; },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Animer quand end change et > 0
  useEffect(() => {
    if (end <= 0) return;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <span ref={ref}>{count.toLocaleString('fr-CH')}{suffix}</span>;
};

// Skeleton loader pour les cartes produits
const ProductSkeleton = () => (
  <div className="product-skeleton">
    <div className="product-skeleton__image skeleton-pulse" />
    <div className="product-skeleton__info">
      <div className="product-skeleton__price skeleton-pulse" />
      <div className="product-skeleton__text skeleton-pulse" />
      <div className="product-skeleton__text short skeleton-pulse" />
    </div>
  </div>
);

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category_id: '',
    subcategory_id: '',
    condition: '',
    city: '',
    minPrice: '',
    maxPrice: '',
    sort: 'newest'
  });
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const hoverTimeoutRef = useRef(null);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [stats, setStats] = useState({ products: 0, users: 0, sold: 0, categories: 0 });
  const rotationRef = useRef(null);
  // Pagination "Charger plus" de la liste publique
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const currentFiltersRef = useRef({});
  const PAGE_SIZE = 24;

  const handleCategoryMouseEnter = useCallback((cat, e) => {
    clearTimeout(hoverTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    setHoveredCategory(cat.id);
  }, []);

  const handleCategoryMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
    }, 150);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
    }, 150);
  }, []);

  // Grouper les produits par catégorie parente (seulement celles avec des articles)
  const groupedByCategory = React.useMemo(() => {
    if (!products.length) return [];
    const groups = {};
    products.forEach(p => {
      const catName = p.parent_category_name || p.category_name || 'Autres';
      const catSlug = p.parent_category_slug || p.category_slug || 'autres';
      if (!groups[catSlug]) {
        groups[catSlug] = { name: catName, slug: catSlug, products: [] };
      }
      groups[catSlug].products.push(p);
    });
    return Object.values(groups);
  }, [products]);

  // Rotation automatique des catégories toutes les 2 secondes
  useEffect(() => {
    if (isFiltered || showAll || groupedByCategory.length <= 1) {
      clearInterval(rotationRef.current);
      return;
    }
    rotationRef.current = setInterval(() => {
      setActiveCategoryIndex(prev => (prev + 1) % groupedByCategory.length);
    }, 2000);
    return () => clearInterval(rotationRef.current);
  }, [groupedByCategory.length, isFiltered]);

  useEffect(() => {
    fetchProducts();
    fetchTheme();
    fetchCategories();
    fetchStats();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await api.get('/theme');
      setTheme(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du theme:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await productsAPI.getCategoriesHierarchy();
      setMainCategories(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await productsAPI.getPublicStats();
      setStats(response.data);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchProducts = async (searchFilters = {}) => {
    try {
      setLoading(true);
      currentFiltersRef.current = searchFilters;
      const response = await productsAPI.getAll({ ...searchFilters, limit: PAGE_SIZE, offset: 0 });
      const list = response.data || [];
      setProducts(list);
      // S'il y a exactement une page pleine, il y a probablement plus à charger
      setHasMore(list.length === PAGE_SIZE);
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreProducts = async () => {
    if (loadingMore) return;
    try {
      setLoadingMore(true);
      const response = await productsAPI.getAll({
        ...currentFiltersRef.current,
        limit: PAGE_SIZE,
        offset: products.length
      });
      const list = response.data || [];
      setProducts(prev => [...prev, ...list]);
      setHasMore(list.length === PAGE_SIZE);
    } catch (error) {
      console.error('Erreur lors du chargement des articles supplémentaires:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setIsFiltered(true);
    setShowAll(false);
    setFilters(prev => ({ ...prev, category_id: category.id, subcategory_id: '' }));
    fetchProducts({ category_id: category.id });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setIsFiltered(true);
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== '')
    );
    fetchProducts(cleanFilters);
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setIsFiltered(false);
    setShowAll(false);
    setActiveCategoryIndex(0);
    setFilters({
      search: '',
      category_id: '',
      subcategory_id: '',
      condition: '',
      city: '',
      minPrice: '',
      maxPrice: '',
      sort: 'newest'
    });
    fetchProducts();
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const conditionLabels = {
    'neuf_etiquette': t('conditions.neuf_etiquette'),
    'neuf_sans_etiquette': t('conditions.neuf_sans_etiquette'),
    'tres_bon': t('conditions.tres_bon'),
    'bon': t('conditions.bon'),
    'satisfaisant': t('conditions.satisfaisant')
  };

  // Appliquer les couleurs du thème via CSS variables
  useEffect(() => {
    if (theme) {
      const root = document.documentElement;
      root.style.setProperty('--theme-primary', theme.primary_color);
      root.style.setProperty('--theme-secondary', theme.secondary_color);
      root.style.setProperty('--theme-bg', theme.background_color);
      root.style.setProperty('--theme-hero-bg', theme.hero_background);
      root.style.setProperty('--theme-hero-text', theme.hero_text_color);
      root.style.setProperty('--theme-header-bg', theme.header_background);
      root.style.setProperty('--theme-btn', theme.button_color);
      root.style.setProperty('--theme-btn-text', theme.button_text_color);
    }
    return () => {
      const root = document.documentElement;
      root.style.removeProperty('--theme-primary');
      root.style.removeProperty('--theme-secondary');
      root.style.removeProperty('--theme-bg');
      root.style.removeProperty('--theme-hero-bg');
      root.style.removeProperty('--theme-hero-text');
      root.style.removeProperty('--theme-header-bg');
      root.style.removeProperty('--theme-btn');
      root.style.removeProperty('--theme-btn-text');
    };
  }, [theme]);

  return (
    <div className="home" style={theme ? { backgroundColor: theme.background_color } : {}}>
      {/* Effet visuel animé */}
      {theme && <VisualEffect effect={theme.visual_effect} />}

      {/* ===== HERO SECTION ===== */}
      <section className="hero">
        <div className="hero__inner">

          {/* Categories dans le hero */}
          <div className="hero__categories-wrapper">
          <div className="hero__categories">
            <button
              className={`hero__cat-btn ${showAll ? 'active' : ''}`}
              onClick={() => {
                setShowAll(true);
                setIsFiltered(false);
                setSelectedCategory(null);
                setFilters(prev => ({ ...prev, category_id: '', subcategory_id: '' }));
                fetchProducts();
              }}
            >
              <span>🏪</span> {t('home.allCategories')}
            </button>
            {mainCategories.map(cat => (
              <div
                key={cat.id}
                className="category-wrapper"
                onMouseEnter={(e) => handleCategoryMouseEnter(cat, e)}
                onMouseLeave={handleCategoryMouseLeave}
              >
                <button
                  className={`hero__cat-btn ${selectedCategory?.id === cat.id ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(cat)}
                >
                  <span>{categoryIcons[cat.slug] || '📦'}</span> {t('categories.' + cat.slug, cat.name)}
                </button>
              </div>
            ))}
          </div>
          </div>

          <form className="hero__search" onSubmit={handleSearch}>
            <input
              type="text"
              name="search"
              placeholder={t('home.searchPlaceholder', 'Rechercher un article...')}
              value={filters.search}
              onChange={handleFilterChange}
              className="hero__search-input"
            />
            <button type="submit" className="hero__search-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              {t('common.search', 'Rechercher')}
            </button>
          </form>
        </div>
      </section>

      {/* Dropdown sous-categories au hover */}
      {hoveredCategory && (() => {
        const cat = mainCategories.find(c => c.id === hoveredCategory);
        if (!cat || !cat.subcategories || cat.subcategories.length === 0) return null;
        return (
          <div
            className="category-dropdown"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            {cat.subcategories.map(sub => (
              <button
                key={sub.id}
                className={`category-dropdown-item ${selectedCategory?.id === cat.id && filters.subcategory_id === sub.id.toString() ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(cat);
                  setIsFiltered(true);
                  setShowAll(false);
                  setFilters(prev => ({ ...prev, category_id: cat.id, subcategory_id: sub.id }));
                  fetchProducts({ subcategory_id: sub.id });
                  setHoveredCategory(null);
                }}
              >
                {t('categories.' + sub.slug, sub.name)}
              </button>
            ))}
          </div>
        );
      })()}

      {/* ===== PRODUITS ===== */}
      <div className="products-section">
        <div className="container">
          {/* Titre de section */}
          <div className="products-section__header">
            <h2 className="products-section__title">
              {isFiltered
                ? t('home.searchResults', 'Resultats de recherche')
                : t('home.latestProducts', 'Derniers articles publies')
              }
            </h2>
            {!loading && products.length > 0 && (
              <span className="products-section__count">
                {t('common.results', { count: products.length })}
              </span>
            )}
          </div>

          {/* Filtres avancés (toggle) */}
          <div className="filters-bar">
            <button
              type="button"
              className="btn-filter-toggle"
              onClick={toggleFilters}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
              </svg>
              {showFilters ? t('home.hideFilters') : t('home.showFilters')}
            </button>
            {isFiltered && (
              <button type="button" onClick={handleReset} className="btn-reset-inline">
                {t('common.reset')}
              </button>
            )}
          </div>

          {showFilters && (
            <form className="filters-advanced-form" onSubmit={handleSearch}>
              <div className="filters-advanced">
                <div className="filter-group">
                  <label>{t('common.category')}</label>
                  <select
                    name="category_id"
                    value={filters.category_id}
                    onChange={(e) => {
                      const catId = e.target.value;
                      const cat = mainCategories.find(c => c.id.toString() === catId);
                      if (cat) {
                        handleCategoryClick(cat);
                      } else {
                        setSelectedCategory(null);
                        setFilters(prev => ({ ...prev, category_id: '', subcategory_id: '' }));
                      }
                    }}
                  >
                    <option value="">{t('home.allCategoriesOption')}</option>
                    {mainCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{t('categories.' + cat.slug, cat.name)}</option>
                    ))}
                  </select>
                </div>

                {selectedCategory && selectedCategory.subcategories && (
                  <div className="filter-group">
                    <label>{t('common.subcategory')}</label>
                    <select
                      name="subcategory_id"
                      value={filters.subcategory_id}
                      onChange={handleFilterChange}
                    >
                      <option value="">{t('home.allSubcategoriesOption')}</option>
                      {selectedCategory.subcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{t('categories.' + sub.slug, sub.name)}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="filter-group">
                  <label>{t('common.condition')}</label>
                  <select
                    name="condition"
                    value={filters.condition}
                    onChange={handleFilterChange}
                  >
                    <option value="">{t('home.allConditions')}</option>
                    {Object.entries(conditionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>{t('common.city')}</label>
                  <input
                    type="text"
                    name="city"
                    placeholder={t('home.cityPlaceholder')}
                    value={filters.city}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="filter-group">
                  <label>{t('home.minPrice')}</label>
                  <input
                    type="number"
                    name="minPrice"
                    placeholder={t('home.minPlaceholder')}
                    value={filters.minPrice}
                    onChange={handleFilterChange}
                    min="0"
                  />
                </div>

                <div className="filter-group">
                  <label>{t('home.maxPrice')}</label>
                  <input
                    type="number"
                    name="maxPrice"
                    placeholder={t('home.maxPlaceholder')}
                    value={filters.maxPrice}
                    onChange={handleFilterChange}
                    min="0"
                  />
                </div>

                <div className="filter-group">
                  <label>{t('home.sortBy')}</label>
                  <select
                    name="sort"
                    value={filters.sort}
                    onChange={handleFilterChange}
                  >
                    <option value="newest">{t('home.newest')}</option>
                    <option value="price_asc">{t('home.priceAsc')}</option>
                    <option value="price_desc">{t('home.priceDesc')}</option>
                    <option value="popular">{t('home.popular')}</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                >
                  {t('common.search')}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn-secondary btn-reset"
                >
                  {t('common.reset')}
                </button>
              </div>
            </form>
          )}

          {/* Resultats */}
          {loading ? (
            <div className="products-grid">
              {Array.from({ length: 10 }).map((_, i) => (
                <ProductSkeleton key={i} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="no-results">
              <div className="no-results__icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </div>
              <p>{t('home.noProducts')}</p>
              <p className="no-results-hint">{t('home.noProductsHint')}</p>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              {hasMore && (
                <div className="load-more-wrap">
                  <button
                    className="load-more-btn"
                    onClick={loadMoreProducts}
                    disabled={loadingMore}
                  >
                    {loadingMore ? t('common.loading') : t('home.loadMore', 'Charger plus')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ===== STATS ===== */}
      {(stats.products > 0 || stats.users > 0) && <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="home-stat">
              <div className="home-stat__value">
                <AnimatedCounter end={stats.products} />
              </div>
              <div className="home-stat__label">{t('home.statsProducts', 'Articles en ligne')}</div>
            </div>
            <div className="home-stat">
              <div className="home-stat__value">
                <AnimatedCounter end={stats.users} />
              </div>
              <div className="home-stat__label">{t('home.statsUsers', 'Membres inscrits')}</div>
            </div>
            <div className="home-stat">
              <div className="home-stat__value">
                <AnimatedCounter end={stats.sold} />
              </div>
              <div className="home-stat__label">{t('home.statsSold', 'Articles vendus')}</div>
            </div>
            <div className="home-stat">
              <div className="home-stat__value">
                <AnimatedCounter end={stats.categories} />
              </div>
              <div className="home-stat__label">{t('home.statsCategories', 'Categories')}</div>
            </div>
          </div>
        </div>
      </section>}

      {/* ===== WHY MAIN2MAIN ===== */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title">{t('home.whyTitle', 'Pourquoi Main2Main ?')}</h2>
          <p className="section-subtitle">{t('home.whySubtitle', 'La marketplace suisse pensee pour vous')}</p>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-card__icon">🆓</div>
              <h3 className="why-card__title">{t('home.whyFree', '100% Gratuit')}</h3>
              <p className="why-card__text">{t('home.whyFreeDesc', 'Publiez vos annonces sans frais. Pas de commission, pas d\'abonnement.')}</p>
            </div>
            <div className="why-card">
              <div className="why-card__icon">🔒</div>
              <h3 className="why-card__title">{t('home.whySecure', 'Paiement securise')}</h3>
              <p className="why-card__text">{t('home.whySecureDesc', 'Paiement par carte via Stripe. Votre argent est protege jusqu\'a la reception.')}</p>
            </div>
            <div className="why-card">
              <div className="why-card__icon">📦</div>
              <h3 className="why-card__title">{t('home.whyDelivery', 'Livraison flexible')}</h3>
              <p className="why-card__text">{t('home.whyDeliveryDesc', 'Envoi par La Poste Suisse ou remise en main propre. A vous de choisir.')}</p>
            </div>
            <div className="why-card">
              <div className="why-card__icon">🇨🇭</div>
              <h3 className="why-card__title">{t('home.whySwiss', '100% Suisse')}</h3>
              <p className="why-card__text">{t('home.whySwissDesc', 'Disponible dans toute la Suisse. En francais, allemand, anglais et chinois.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-card__content">
              <h2 className="cta-card__title">{t('home.ctaTitle', 'Pret a vendre ?')}</h2>
              <p className="cta-card__text">
                {t('home.ctaDesc', 'Vos objets meritent une seconde vie. Publiez votre premiere annonce gratuitement en quelques clics.')}
              </p>
              <button className="cta-card__btn" onClick={() => navigate('/create-product')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                {t('home.ctaBtn', 'Vendre un article')}
              </button>
            </div>
            <div className="cta-card__visual">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Avis Google */}
      <GoogleReviews />
    </div>
  );
};

export default Home;
